import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import NavBarPage from '../pages/NavBarPage';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import useActionItemSignalR from '../hooks/useActionItemSignalR';
import SentryFeedbackButton from './SentryFeedbackButton';
import * as Sentry from '@sentry/react';

const Footer = () => {
    const [version, setVersion] = useState('');
    const { apiFetch } = useAuth();

    useEffect(() => {
        let ignore = false;
        const getVersion = async () => {
            try {
                const res = await apiFetch('/api/system/version', {}, 10000);
                if (!res.ok) return;
                const json = await res.json();
                if (!ignore) setVersion(json?.version || '');
            }
            catch (catchError) {
                Sentry.captureException(catchError);
                /* ignore */
            }
        };
        getVersion();
        return () => { ignore = true; };
    }, [apiFetch]);

    const displayVersion = version
        ? version
            .replace(/^captrac-reactcore@/i, '')
            .replace(/[-+].*$/, '')
        : '';

    return (
        <footer className="footer">
            <div className="footer-content">
                {displayVersion && (
                    <span style={{ color: '#fff', fontWeight: 700 }}>CapTrac Version: {displayVersion}</span>
                )}
            </div>
            <div className="footer-logo">
                <img
                    src="https://www.capland.co.za/wp-content/uploads/2016/08/CapitalLand-logo-footer.png"
                    alt="Capital Land Footer Logo"
                    className="footer-img"
                />
            </div>
        </footer>
    );
};

const ProtectedLayout = () => {
    useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useLayoutEffect(() => {
        // Development-only navigation trace to avoid unnecessary logging and sync work in production.
        if (import.meta.env.DEV) {
            //console.log(`[Layout useLayoutEffect - SYNC] ${location.pathname} @ ${new Date().toISOString()}`);
        }
    }, [location.pathname]);
    /**
     * Handles "needs attention" SignalR notifications.
     * Toast is shown immediately — no API call is made before displaying it.
     * Previously this awaited fetchActionItemTitle() which went through apiFetch,
     * triggering token refresh and blocking all concurrent API calls.
     */
    const handleGlobalAttention = useCallback((update) => {
        const actionItemId = update?.actionItemId || update?.ActionItemId;
        if (!actionItemId) return;

        const currentPath = window.location.pathname;
        if (currentPath === `/action-items/${actionItemId}`) return;

        const updateType = update?.type || update?.Type || 'Updated';
        const newStatus = update?.newStatus || update?.NewStatus;
        const oldStatus = update?.oldStatus || update?.OldStatus;

        let changeDescription = 'Updated';
        if (updateType === 'Created') {
            changeDescription = 'New action item created';
        } else if (updateType === 'StatusChange' && newStatus && oldStatus) {
            changeDescription = `Status: ${oldStatus} → ${newStatus}`;
        } else if (updateType === 'PriorityChange') {
            const newPriority = update?.data?.newPriority || update?.Data?.NewPriority;
            if (newPriority) changeDescription = `Priority changed to ${newPriority}`;
        } else if (updateType === 'AssignmentChange') {
            changeDescription = 'Assignment changed';
        } else if (updateType === 'Comment') {
            changeDescription = 'New comment added';
        }

        toast.warning(
            <div onClick={() => navigate(`/action-items/${actionItemId}`)} style={{ cursor: 'pointer' }}>
                <strong>Action Item #{actionItemId}</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>{changeDescription}</div>
                <small style={{ opacity: 0.8 }}>Click to view</small>
            </div>,
            { position: 'top-right', autoClose: 7000 }
        );
    }, [navigate]);

    /**
     * Handles "assigned to you" SignalR notifications.
     * Toast is shown immediately — no API call is made before displaying it.
     */
    const handleGlobalAssignment = useCallback((update) => {
        const actionItemId = update?.actionItemId || update?.ActionItemId;
        if (!actionItemId) return;

        toast.info(
            <div onClick={() => navigate(`/action-items/${actionItemId}`)} style={{ cursor: 'pointer' }}>
                <strong>New Assignment</strong>
                <div>Action Item #{actionItemId}</div>
                <small style={{ opacity: 0.8 }}>Click to view</small>
            </div>,
            { position: 'top-right', autoClose: 7000 }
        );
    }, [navigate]);

    const { isConnected } = useActionItemSignalR({
        // onActionItemUpdated is broadcast to ALL connected clients via the "all" group.
        // It should NOT show toast notifications — those are handled by the targeted
        // onNeedsAttention and onAssignedToYou events which are scoped to the correct user.
        onNeedsAttention: handleGlobalAttention,
        onAssignedToYou: handleGlobalAssignment,
        enabled: true,
    });

    useEffect(() => {
        //console.log('[SignalR Global] Connection status:', isConnected ? 'Connected' : 'Disconnected');
    }, [isConnected]);

    return (
        <div className="layout-root">
            <NavBarPage />
            <div className="main-content" key={location.pathname}>
                <Outlet />
            </div>
            <Footer />
            <SentryFeedbackButton />
        </div>
    );
};

export default ProtectedLayout;